import { useMemo } from 'react';
import {
  CodeLink,
  CodeRepoReviewStatus,
  RepoType,
  isCodeRepoReviewStatus,
  isRepoType,
} from '@amzn/awscat-pattern-library-service-model';

import { useCurrentUser } from '../../contexts/UserContext';
import { isForInternalContentAudience } from '../workflow-viewer/content/ContentUtils';
import { useAudience } from '../v2-hooks/useAudience';
import { useCoAuthors } from '../v2-hooks/useCoAuthors';
import { useContent } from '../v2-hooks/useContent';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';
import {
  ValidationResult,
  validateAppsecTicketUrl,
  validateOpenSourceTicketUrl,
} from '../../services/CodeRepoValidation';

/**
 * Returns the trimmed ticket url when it passes the editor's ticket rules, else
 * blank. Stored values are re-checked here because the editor check runs only
 * in the browser, so a stored `javascript:` or otherwise invalid url must not reach a link.
 */
function toSafeTicketUrl(
  ticket: string | undefined,
  validate: (raw: string) => ValidationResult,
): string {
  if (!ticket || !validate(ticket).valid) return '';
  return ticket.trim();
}

function toReadonlyRow(codeLink: CodeLink, index: number): CodeReadonlyRow {
  return {
    key: String(index),
    url: codeLink.url ?? '',
    label: codeLink.label ?? '',
    branch: codeLink.branch ?? '',
    repoType:
      codeLink.repoType && isRepoType(codeLink.repoType) ? codeLink.repoType : RepoType.Internal,
    appsecStatus:
      codeLink.appsecStatus && isCodeRepoReviewStatus(codeLink.appsecStatus)
        ? codeLink.appsecStatus
        : CodeRepoReviewStatus.NotStarted,
    appsecTicketUrl: toSafeTicketUrl(codeLink.appsecTicket, validateAppsecTicketUrl),
    openSourceStatus:
      codeLink.openSourceStatus && isCodeRepoReviewStatus(codeLink.openSourceStatus)
        ? codeLink.openSourceStatus
        : CodeRepoReviewStatus.NotStarted,
    openSourceTicketUrl: toSafeTicketUrl(codeLink.openSourceTicket, validateOpenSourceTicketUrl),
  };
}

/** One read-only code repository row. */
export interface CodeReadonlyRow {
  /** Stable, unique row key; code links carry no id and urls may be blank or repeated. */
  readonly key: string;
  readonly url: string;
  readonly label: string;
  readonly branch: string;
  readonly repoType: RepoType;
  readonly appsecStatus: CodeRepoReviewStatus;
  /** AppSec / Security Guardian review ticket; blank when none was recorded or it fails url validation. */
  readonly appsecTicketUrl: string;
  readonly openSourceStatus: CodeRepoReviewStatus;
  /** Open-source review ticket; blank when none was recorded or it fails url validation. */
  readonly openSourceTicketUrl: string;
}

/** Read-only presentation state for the Code tab on the view page. */
export interface CodeReadonlyTabViewModel {
  readonly rows: readonly CodeReadonlyRow[];
  /** True when the audience is external; gates display of the open-source review column. */
  readonly isExternalAudience: boolean;
  /** True when the current user may follow review ticket links; general readers see status only. */
  readonly isReviewTicketLinkVisible: boolean;
}

/**
 * Projects the loaded content's code links into read-only display rows.
 * Returns undefined while content is not loaded — the view renders nothing
 * during that transient state rather than crashing.
 */
export function useCodeReadonlyTabViewModel(): CodeReadonlyTabViewModel | undefined {
  const content = useContent();
  const audience = useAudience();
  const loadedDocument = useLoadedDocument();
  const coAuthors = useCoAuthors();
  const { canSeeCodeReviewTicketsForContent } = useCurrentUser();

  // Compute rows before any early exit so hook order stays stable across
  // renders where content transitions between loaded and undefined.
  const rows = useMemo(() => {
    const codeLinks = content?.contentTypeMetadata?.codeLinks ?? [];
    return codeLinks.map(toReadonlyRow);
  }, [content]);

  // When the owner or co-authors change, re-evaluate ticket link visibility.
  const isReviewTicketLinkVisible = useMemo(
    () =>
      canSeeCodeReviewTicketsForContent(
        loadedDocument?.author.userId,
        coAuthors.map((coAuthor) => coAuthor.userId),
      ),
    [canSeeCodeReviewTicketsForContent, loadedDocument, coAuthors],
  );

  // A parent-teardown render can transiently clear content before this view
  // unmounts; return undefined so the view renders null instead of throwing.
  if (!content || !audience) {
    return undefined;
  }

  return {
    rows,
    isExternalAudience: !isForInternalContentAudience(audience),
    isReviewTicketLinkVisible,
  };
}
